import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { uploadAndParseDocument } from '@/lib/documentParser';
import { reportClientError } from '@/lib/errorReporter';
import { fetchTenantScopedCompanies, resolveTenantId } from '@/lib/tenantScope';
import {
  clearTemplateDraft,
  createTemplateDraftKey,
  loadTemplateDraft,
  saveTemplateDraft,
} from '@/lib/templateDraftStorage';

interface KnowledgeSource {
  name: string;
  url: string;
  extracted_text?: string;
  word_count?: number;
}

type CoachingTemplateCategory =
  | 'sales'
  | 'strategy'
  | 'people'
  | 'communication'
  | 'education'
  | 'professional_dev'
  | 'professional_development';

const MAX_TEMPLATE_UPLOAD_BYTES = 20 * 1024 * 1024;
const TEMPLATE_DRAFT_MAX_AGE_DAYS = 30;
const SUPPORTED_TEMPLATE_EXTENSIONS = ['pdf', 'docx', 'doc', 'txt'];

function formatFileSize(bytes: number): string {
  const mib = bytes / (1024 * 1024);
  const mb = bytes / 1_000_000;
  return `${mib.toFixed(1)} MiB (${mb.toFixed(1)} MB)`;
}

function getFileExtension(fileName: string): string {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
}

function normaliseTemplateCategory(category: CoachingTemplateCategory): string {
  return category === 'professional_development' || category === 'professional_dev' ? 'education' : category;
}

function deriveTemplateIdentifiers(templateName: string) {
  const trimmedName = templateName.trim();
  const templateType = trimmedName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'custom_template';

  return {
    templateType,
    outputDocument: trimmedName,
  };
}

export default function CreateCoachingTemplate() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const currentTenantId = resolveTenantId(tenant, user?.tenant_id);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [assignTo, setAssignTo] = useState<'all' | 'specific_companies'>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  
  // Document upload state — two types for different purposes
  const [designTemplates, setDesignTemplates] = useState<KnowledgeSource[]>([]);
  const [instructionDocs, setInstructionDocs] = useState<KnowledgeSource[]>([]);
  const designTemplateInputRef = useRef<HTMLInputElement>(null);
  const instructionDocInputRef = useRef<HTMLInputElement>(null);
  const draftReadyRef = useRef(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'sales' as CoachingTemplateCategory,
    template_type: '',
    estimated_duration: 20,
    output_document: '',
    system_prompt: '',
    document_format: 'docx'
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', currentTenantId, isMasterTenant],
    queryFn: () => fetchTenantScopedCompanies({
      tenantId: currentTenantId,
      isMasterTenant,
      select: 'id, name',
    }),
    enabled: hasRole('admin') && (isMasterTenant || !!currentTenantId)
  });

  const draftKey = user?.id && currentTenantId
    ? createTemplateDraftKey({ area: 'output-template', tenantId: currentTenantId, userId: user.id })
    : null;

  const draftHasContent = () => (
    formData.name.trim() ||
    formData.description.trim() ||
    formData.system_prompt.trim() ||
    designTemplates.length > 0 ||
    instructionDocs.length > 0
  );

  const persistDraft = (showToast = false) => {
    if (!draftKey || !draftHasContent()) return false;

    const saved = saveTemplateDraft(window.localStorage, draftKey, {
      formData,
      assignTo,
      selectedCompanyId,
      designTemplates,
      instructionDocs,
    });

    if (showToast) {
      if (saved) {
        toast.success('Draft saved');
      } else {
        toast.error('Unable to save draft in this browser');
      }
    }

    return Boolean(saved);
  };

  useEffect(() => {
    if (!draftKey || draftReadyRef.current) return;

    const draft = loadTemplateDraft<{
      formData?: typeof formData;
      assignTo?: typeof assignTo;
      selectedCompanyId?: string | null;
      designTemplates?: KnowledgeSource[];
      instructionDocs?: KnowledgeSource[];
    }>(window.localStorage, draftKey, { maxAgeDays: TEMPLATE_DRAFT_MAX_AGE_DAYS });

    if (draft) {
      if (draft.payload.formData) setFormData(prev => ({ ...prev, ...draft.payload.formData }));
      if (draft.payload.assignTo) setAssignTo(draft.payload.assignTo);
      setSelectedCompanyId(draft.payload.selectedCompanyId || null);
      setDesignTemplates(draft.payload.designTemplates || []);
      setInstructionDocs(draft.payload.instructionDocs || []);
      toast.info('Restored your saved output template draft');
    }

    draftReadyRef.current = true;
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReadyRef.current || !draftHasContent()) return;

    const timeout = window.setTimeout(() => {
      persistDraft(false);
    }, 900);

    return () => window.clearTimeout(timeout);
  }, [draftKey, formData, assignTo, selectedCompanyId, designTemplates, instructionDocs]);

  // Generic file upload handler for both document types
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'design' | 'instructions',
    setter: React.Dispatch<React.SetStateAction<KnowledgeSource[]>>,
    currentList: KnowledgeSource[],
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = type === 'design' ? 1 : 5;

    if (currentList.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    for (const file of files) {
      const extension = getFileExtension(file.name);

      if (!SUPPORTED_TEMPLATE_EXTENSIONS.includes(extension)) {
        const message = `${file.name} is not supported. Please upload PDF, DOCX, DOC, or TXT.`;

        toast.error(message);
        reportClientError(new Error(message), {
          area: 'output_template_upload',
          action: 'unsupported_file_type',
          severity: 'warning',
          metadata: { fileName: file.name, extension, size: file.size, uploadType: type },
        });
        continue;
      }

      if (file.size > MAX_TEMPLATE_UPLOAD_BYTES) {
        const message = `${file.name} is ${formatFileSize(file.size)}. Maximum is 20 MiB.`;
        toast.error(message);
        reportClientError(new Error(message), {
          area: 'output_template_upload',
          action: 'file_too_large',
          severity: 'warning',
          metadata: { fileName: file.name, size: file.size, uploadType: type },
        });
        continue;
      }

      try {
        toast.loading(`Uploading ${file.name}...`, { id: `upload-${type}-${file.name}` });
        const folder = type === 'design' ? 'coaching-templates/design' : 'coaching-templates/instructions';
        const result = await uploadAndParseDocument(file, 'knowledge-sources', folder);

        if (!result.success) {
          const message = result.error || 'Unknown upload error';
          toast.error(`Failed to upload ${file.name}: ${message}`, { id: `upload-${type}-${file.name}` });
          reportClientError(new Error(message), {
            area: 'output_template_upload',
            action: 'upload_failed',
            severity: 'error',
            metadata: { fileName: file.name, size: file.size, uploadType: type },
          });
          continue;
        }

        // For design templates, replace the existing one (only 1 allowed)
        if (type === 'design') {
          setter([{
            name: result.name,
            url: result.url,
            extracted_text: result.extracted_text,
            word_count: result.word_count
          }]);
        } else {
          setter(prev => [...prev, {
            name: result.name,
            url: result.url,
            extracted_text: result.extracted_text,
            word_count: result.word_count
          }]);
        }

        if (result.warning) {
          toast.warning(`${file.name} uploaded, but text extraction needs attention: ${result.warning}`, {
            id: `upload-${type}-${file.name}`,
            duration: 8000,
          });
          reportClientError(new Error(result.warning), {
            area: 'output_template_upload',
            action: 'parse_warning',
            severity: 'warning',
            metadata: { fileName: file.name, size: file.size, uploadType: type },
          });
        } else {
          toast.success(`${file.name} uploaded`, { id: `upload-${type}-${file.name}` });
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          id: `upload-${type}-${file.name}`,
        });
        reportClientError(error, {
          area: 'output_template_upload',
          action: 'upload_exception',
          severity: 'error',
          metadata: { fileName: file.name, size: file.size, uploadType: type },
        });
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    // Auto-derive the internal slug and document title so authors only fill in
    // Template Name. Both values remain on the row for existing readers.
    const { templateType, outputDocument } = deriveTemplateIdentifiers(formData.name);

    setLoading(true);
    try {
      const isAdmin = hasRole('admin');
      const finalCompanyId = isAdmin 
        ? (assignTo === 'specific_companies' ? selectedCompanyId : null)
        : user?.company_id || null;
      const finalAssignTo = isAdmin ? assignTo : 'specific_companies';

      // Build sample_documents array with type tags and pre-extracted text
      const sampleDocuments = [
        ...designTemplates.map(d => ({
          name: d.name,
          url: d.url,
          type: 'design' as const,
          extracted_text: d.extracted_text || '',
          word_count: d.word_count || 0,
        })),
        ...instructionDocs.map(d => ({
          name: d.name,
          url: d.url,
          type: 'instructions' as const,
          extracted_text: d.extracted_text || '',
          word_count: d.word_count || 0,
        })),
      ];

      // RLS policy `tenant_isolation_coaching_templates` requires tenant_id to
      // match either MASTER_TENANT_ID or the inserter's own tenant. Without this,
      // tenant admins (e.g. Murray on Boston Scientific) silently get blocked by RLS.
      const finalTenantId = currentTenantId;
      if (!finalTenantId) {
        toast.error('Unable to determine your tenant. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const { error } = await supabase.from('coaching_templates').insert([{
        name: formData.name,
        description: formData.description,
        category: normaliseTemplateCategory(formData.category),
        template_type: templateType,
        estimated_duration: formData.estimated_duration,
        output_document: outputDocument,
        system_prompt: formData.system_prompt,
        document_format: formData.document_format,
        is_default: false,
        is_active: true,
        assign_to: finalAssignTo,
        company_id: finalCompanyId,
        tenant_id: finalTenantId,
        sample_documents: sampleDocuments,
        intake_script: JSON.parse(JSON.stringify({
          phases: [],
          totalQuestions: 0,
          estimatedDuration: formData.estimated_duration,
        })),
        document_template: { title: outputDocument, sections: [] }
      }]);

      if (error) throw error;

      if (draftKey) clearTemplateDraft(window.localStorage, draftKey);
      
      toast.success('Output template created successfully');
      queryClient.invalidateQueries({ queryKey: ['coaching-templates'] });
      navigate('/admin/coaching-templates');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create template');
      reportClientError(error, {
        area: 'output_template_create',
        action: 'submit_failed',
        severity: 'error',
        metadata: {
          templateName: formData.name,
          tenantId: currentTenantId,
          designTemplateCount: designTemplates.length,
          instructionDocCount: instructionDocs.length,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/coaching-templates')} className="mb-4 -ml-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Output Templates
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Create Output Template</h1>
          <p className="text-muted-foreground mt-1">Define a new document output template for coaching conversations</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
              <CardDescription>Basic information about this output template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Sales Pre-Call Coach"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this coaching template helps users with..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v: any) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">📈 Sales</SelectItem>
                    <SelectItem value="strategy">🎯 Strategy</SelectItem>
                    <SelectItem value="people">👥 People</SelectItem>
                    <SelectItem value="communication">💬 Communication</SelectItem>
                    <SelectItem value="education">🎓 Education / Professional Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Template Type Key is now auto-derived from Template Name on submit
                  (lowercased + underscored). Per Anthony's feedback, the form
                  previously asked the user to fill three near-identical name
                  fields; this was confusing. The slug is internal-only and never
                  shown to learners. */}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Estimated Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="5"
                    max="120"
                    value={formData.estimated_duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration: parseInt(e.target.value) || 20 }))}
                  />
                </div>

                <div>
                  <Label>Document Format</Label>
                  <Select value={formData.document_format} onValueChange={(v) => setFormData(prev => ({ ...prev, document_format: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="docx">📄 Word Document (.docx)</SelectItem>
                      <SelectItem value="pdf">📑 PDF Document</SelectItem>
                      <SelectItem value="markdown">📝 Markdown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                The internal template key and generated document title are created automatically from the Template Name.
              </p>
            </CardContent>
          </Card>

          {/* Design Template — the exact document layout to replicate */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Design Template
              </CardTitle>
              <CardDescription>
                Upload the exact document you want the AI to replicate. The output will match this document's
                structure, headings, sections, and format as closely as possible.
                (e.g., Business Model Canvas, Sales Call Planning Card, Performance Review Form)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer bg-primary/5"
                onClick={() => designTemplateInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-primary/60 mb-2" />
                <p className="text-sm font-medium text-foreground">
                  Upload your design template (PDF, DOCX, TXT)
                </p>
                <p className="text-xs text-muted-foreground mt-1">1 file, 20MB max — the AI will replicate this exact format</p>
                <input
                  ref={designTemplateInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={(e) => handleFileUpload(e, 'design', setDesignTemplates, designTemplates, designTemplateInputRef)}
                  className="hidden"
                />
              </div>

              {designTemplates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {designTemplates.map((file, index) => (
                    <Badge key={index} variant="default" className="flex items-center gap-2 py-1.5">
                      <FileText className="w-3 h-3" />
                      {file.name}
                      {file.word_count ? <span className="text-xs opacity-70">({file.word_count} words)</span> : null}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-destructive"
                        onClick={() => setDesignTemplates([])}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions Document — detailed instructions for the AI */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Instructions Document (Optional)
              </CardTitle>
              <CardDescription>
                Upload documents that explain HOW the output should be written — tone, content guidelines,
                what to include or exclude, scoring rules, etc. These guide the AI's behaviour, not the visual layout.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => instructionDocInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Upload instruction documents (PDF, DOCX, TXT)
                </p>
                <p className="text-xs text-muted-foreground mt-1">Max 5 files, 20MB each</p>
                <input
                  ref={instructionDocInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  multiple
                  onChange={(e) => handleFileUpload(e, 'instructions', setInstructionDocs, instructionDocs, instructionDocInputRef)}
                  className="hidden"
                />
              </div>

              {instructionDocs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {instructionDocs.map((file, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-2 py-1.5">
                      <FileText className="w-3 h-3" />
                      {file.name}
                      {file.word_count ? <span className="text-xs opacity-70">({file.word_count} words)</span> : null}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-destructive"
                        onClick={() => setInstructionDocs(prev => prev.filter((_, i) => i !== index))}
                      />
                    </Badge>
                  ))}
                </div>
              )}

              <div>
                <Label htmlFor="system_prompt">Additional Instructions (Optional)</Label>
                <Textarea
                  id="system_prompt"
                  value={formData.system_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder="Type any additional instructions for the AI here... e.g., 'Always include a risk assessment section' or 'Use formal Australian English'"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  These instructions are combined with any uploaded instruction documents
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Visibility & Access - Only show to super admins */}
          {hasRole('admin') && (
            <Card>
              <CardHeader>
                <CardTitle>Visibility & Access</CardTitle>
                <CardDescription>Control who can see and use this template</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Access Control</Label>
                  <Select value={assignTo} onValueChange={(v: any) => setAssignTo(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">🌐 {hasEnterpriseHierarchy ? 'All Branches' : 'All Companies'}</SelectItem>
                      <SelectItem value="specific_companies">🏢 {hasEnterpriseHierarchy ? 'Specific Branch' : 'Specific Company'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {assignTo === 'specific_companies' && (
                  <div>
                    <Label>{hasEnterpriseHierarchy ? 'Branch' : 'Company'}</Label>
                    <Select value={selectedCompanyId || ''} onValueChange={setSelectedCompanyId}>
                      <SelectTrigger>
                        <SelectValue placeholder={hasEnterpriseHierarchy ? 'Select a branch...' : 'Select a company...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/coaching-templates')} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => persistDraft(true)}
              className="flex-1"
              disabled={!draftKey || !draftHasContent()}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button type="submit" className="flex-1" disabled={loading || !formData.name.trim()}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
